import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Form,
    Input,
    Select,
    Card,
    Typography,
    Row,
    Col,
    Space,
    Modal,
    message,
    Spin,
    Popconfirm,
    DatePicker,
    Upload,
    Avatar
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, UploadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import useTrainers from '../../hooks/useTrainers';
import { useBranches } from '../../hooks/useBranches';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Coaches = () => {
    const { 
        trainers, 
        loading, 
        error,
        fetchTrainers, 
        createTrainer, 
        editTrainer, 
        removeTrainer,
        clearTrainerError 
    } = useTrainers();
    
    const { branches } = useBranches();
    const selectedBranch = useSelector((state) => state.branches.selectedBranch);
    const selectedPlace = useSelector((state) => state.branches.selectedPlace);

    const [showAddForm, setShowAddForm] = useState(false);
    const [editingTrainer, setEditingTrainer] = useState(null);
    const [form] = Form.useForm();
    const [photoFile, setPhotoFile] = useState(null);
    const [photoUrl, setPhotoUrl] = useState('');

    useEffect(() => {
        fetchTrainers();
        // Kapsam degisince antrenor listesi de yenilenmeli.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBranch?.id, selectedPlace?.id]);

    useEffect(() => {
        if (error) {
            message.error(error);
            clearTrainerError();
        }
    }, [error]);

    const handlePhotoUpload = (info) => {
        if (info.file.status === 'uploading') {
            return;
        }
        if (info.file.status === 'done') {
            setPhotoFile(info.file.originFileObj);
            setPhotoUrl(URL.createObjectURL(info.file.originFileObj));
        }
    };

    const handlePhotoRemove = () => {
        setPhotoFile(null);
        setPhotoUrl('');
        form.setFieldsValue({ trainerPhoto: undefined });
    };

    const handlePhotoUrlChange = (e) => {
        setPhotoUrl(e.target.value);
    };

    const handleAddTrainer = async (values) => {
        try {
            const trainerData = {
                fullName: values.fullName,
                email: values.email,
                birthdate: values.birthdate?.toISOString(),
                gender: values.gender,
                phoneNumber: values.phoneNumber,
                address: values.address,
                imagePath: photoUrl || '',
                branchIds: values.branchIds || [],
                placeId: selectedPlace?.id,
                educationInformation: values.educationInformation ? 
                    values.educationInformation.map((name, index) => ({
                        name: name,
                        order: index
                    })) : []
            };

            await createTrainer(trainerData).unwrap();
            message.success('Antrenör başarıyla eklendi!');
            form.resetFields();
            setShowAddForm(false);
            setPhotoFile(null);
            setPhotoUrl('');
            fetchTrainers();
        } catch (error) {
            console.error('Add trainer error:', error);
            message.error(`Antrenör eklenirken bir hata oluştu: ${error.message || error}`);
        }
    };

    const handleEditTrainer = async (values) => {
        try {
            const trainerData = {
                id: editingTrainer.id,
                fullName: values.fullName,
                email: values.email,
                birthdate: values.birthdate?.toISOString(),
                gender: values.gender,
                phoneNumber: values.phoneNumber,
                address: values.address,
                imagePath: photoUrl || '',
                branchIds: values.branchIds || [],
                placeId: selectedPlace?.id,
                educationInformation: values.educationInformation ? 
                    values.educationInformation.map((name, index) => ({
                        name: name,
                        order: index
                    })) : []
            };

            await editTrainer(trainerData).unwrap();
            message.success('Antrenör başarıyla güncellendi!');
            form.resetFields();
            setShowAddForm(false);
            setEditingTrainer(null);
            setPhotoFile(null);
            setPhotoUrl('');
            fetchTrainers();
        } catch (error) {
            console.error('Update trainer error:', error);
            message.error(`Antrenör güncellenirken bir hata oluştu: ${error.message || error}`);
        }
    };

    const handleDeleteTrainer = async (id) => {
        try {
            await removeTrainer(id).unwrap();
            message.success('Antrenör başarıyla silindi!');
            fetchTrainers();
        } catch (error) {
            console.error('Delete trainer error:', error);
            message.error(`Antrenör silinirken bir hata oluştu: ${error.message || error}`);
        }
    };

    const showEditModal = (trainer) => {
        setEditingTrainer(trainer);
        setPhotoUrl(trainer.imagePath || '');
        setPhotoFile(null);
        
        // Branches array'ini branchIds'e çevir
        const branchIds = trainer.branches ? 
            branches
                .filter(branch => trainer.branches.includes(branch.name))
                .map(branch => branch.id) : [];
        
        form.setFieldsValue({
            fullName: trainer.fullName,
            email: trainer.email,
            birthdate: trainer.birthdate ? dayjs(trainer.birthdate) : null,
            gender: trainer.gender,
            phoneNumber: trainer.phoneNumber,
            address: trainer.address,
            trainerPhoto: trainer.imagePath || '',
            branchIds: branchIds,
            educationInformation: trainer.educationInformation ? 
                trainer.educationInformation.map(edu => edu.name) : []
        });
        setShowAddForm(true);
    };

    const handleCancel = () => {
        setShowAddForm(false);
        setEditingTrainer(null);
        setPhotoFile(null);
        setPhotoUrl('');
        form.resetFields();
    };

    const columns = [
        {
            title: 'Antrenör Adı',
            dataIndex: 'fullName',
            key: 'fullName',
            fixed: 'left',
        },
        {
            title: 'E-posta',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Telefon',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
        },
        {
            title: 'Doğum Tarihi',
            dataIndex: 'birthdate',
            key: 'birthdate',
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
        },
        {
            title: 'Şubeler',
            dataIndex: 'branches',
            key: 'branches',
            render: (branches) => (
                <Space size={[0, 8]} wrap>
                    {branches && branches.map((branchName, index) => (
                        <span key={index} style={{ 
                            background: '#1890ff', 
                            color: 'white',
                            padding: '4px 8px', 
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500'
                        }}>
                            {branchName}
                        </span>
                    ))}
                </Space>
            ),
        },
        {
            title: 'İşlemler',
            key: 'actions',
            fixed: 'right',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => showEditModal(record)}
                    />
                    <Popconfirm
                        title="Antrenörü silmek istediğinizden emin misiniz?"
                        onConfirm={() => handleDeleteTrainer(record.id)}
                        okText="Evet"
                        cancelText="Hayır"
                    >
                        <Button
                            type="primary"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Card>
                <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
                    <Col>
                        <Title level={3} style={{ margin: 0 }}>
                            Antrenörler
                        </Title>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setShowAddForm(true)}
                        >
                            Yeni Antrenör Ekle
                        </Button>
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={trainers}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 1200 }}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} / ${total} kayıt`,
                    }}
                />

                <Modal
                    title={editingTrainer ? 'Antrenör Düzenle' : 'Yeni Antrenör Ekle'}
                    open={showAddForm}
                    onCancel={handleCancel}
                    footer={null}
                    width={600}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={editingTrainer ? handleEditTrainer : handleAddTrainer}
                    >
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="fullName"
                                    label="Ad Soyad"
                                    rules={[{ required: true, message: 'Lütfen ad soyad giriniz!' }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="email"
                                    label="E-posta"
                                    rules={[
                                        { type: 'email', message: 'Geçerli bir e-posta adresi giriniz!' }
                                    ]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="phoneNumber"
                                    label="Telefon"
                                    rules={[{ required: true, message: 'Lütfen telefon giriniz!' }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="gender"
                                    label="Cinsiyet"
                                    rules={[{ required: true, message: 'Lütfen cinsiyet seçiniz!' }]}
                                >
                                    <Select placeholder="Cinsiyet seçiniz">
                                        <Option value="Erkek">Erkek</Option>
                                        <Option value="Kadın">Kadın</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="birthdate"
                                    label="Doğum Tarihi"
                                    rules={[{ required: true, message: 'Lütfen doğum tarihi giriniz!' }]}
                                >
                                    <DatePicker style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="trainerPhoto"
                                    label="Fotoğraf URL"
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <Avatar 
                                            size={64} 
                                            src={photoUrl} 
                                            icon={<UserOutlined />}
                                        />
                                        <Input 
                                            placeholder="Fotoğraf URL'si giriniz"
                                            value={photoUrl}
                                            onChange={handlePhotoUrlChange}
                                            style={{ flex: 1 }}
                                        />
                                        {photoUrl && (
                                            <Button 
                                                type="text" 
                                                danger 
                                                onClick={handlePhotoRemove}
                                                size="small"
                                            >
                                                Temizle
                                            </Button>
                                        )}
                                    </div>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            name="branchIds"
                            label="Şubeler"
                        >
                            <Select
                                mode="multiple"
                                placeholder="Şube seçiniz (opsiyonel)"
                                style={{ width: '100%' }}
                            >
                                {branches.map(branch => (
                                    <Option key={branch.id} value={branch.id}>
                                        {branch.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="address"
                            label="Adres"
                        >
                            <TextArea rows={3} />
                        </Form.Item>

                        <Form.Item
                            name="educationInformation"
                            label="Eğitim Bilgileri"
                        >
                            <Select
                                mode="tags"
                                placeholder="Eğitim bilgilerini giriniz"
                                style={{ width: '100%' }}
                            />
                        </Form.Item>

                        <Form.Item>
                            <Space>
                                <Button type="primary" htmlType="submit" loading={loading}>
                                    {editingTrainer ? 'Güncelle' : 'Ekle'}
                                </Button>
                                <Button onClick={handleCancel}>
                                    İptal
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Modal>
            </Card>
        </div>
    );
};

export default Coaches;